import {Align, Justify, PositionType} from '@wonderlandengine/react-ui';
import {
    Container,
    Image,
    MaterialContext,
    Panel9Slice,
    PanelProps,
    Text,
} from '@wonderlandengine/react-ui/components';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {FlatOpaqueTextured, UIText} from '../../types/materials.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Emitter, Material} from '@wonderlandengine/api';
import {insertNewlines, parseColor} from '../utils/utils.js';
import {NotifyPropertyChanged} from '../../utils/NotifyPropertyChanged.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {MenuButton} from '../components/menu-button.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {Localization} from '../../utils/Localization.js';

export enum PopupWindowPanelStyle {
    ingame,
    menu,
    menu2,
}

type possibleProps = 'isVisible' | 'text' | 'showButton' | 'regularText';

let LightTextMaterial: Material;

export class PopupWindowController implements NotifyPropertyChanged {
    private static _instance: PopupWindowController;

    static get instance(): PopupWindowController {
        if (!PopupWindowController._instance) {
            PopupWindowController._instance = new PopupWindowController();
        }
        return PopupWindowController._instance;
    }

    private constructor() {}

    private _isVisible = false;
    get isVisible() {
        return this._isVisible;
    }
    private set isVisible(value: boolean) {
        if (value === this._isVisible) return;
        this._isVisible = value;
        this.notifyPropertyChanged('isVisible');
    }

    _regularText: boolean;
    get regularText() {
        return this._regularText;
    }
    private set regularText(value: boolean) {
        this._regularText = value;
        this.notifyPropertyChanged('regularText');
    }

    private _text: string = '';
    get text() {
        return this._text;
    }
    private set text(value: string) {
        this._text = value;
        this.notifyPropertyChanged('text');
    }

    private _showButton = false;
    get showButton() {
        return this._showButton;
    }
    private set showButton(value: boolean) {
        this._showButton = value;
        this.notifyPropertyChanged('showButton');
    }

    propertyChanged: Emitter<[possibleProps]> = new Emitter();

    notifyPropertyChanged(propertyName: possibleProps): void {
        this.propertyChanged.notify(propertyName);
    }

    /**
     * Shows the popup window with the given text.
     *
     * @param {string} text - The text to display in the popup window.
     * @param {boolean} showButton - Whether to show the button in the popup window.
     * @param {boolean} [regularText=false] - Whether to use regular text formatting.
     * Default text is capitalized normal text, without diacritics. To get diacritics, set this to true.
     */
    show(text: string, showButton: boolean, regularText: boolean = false) {
        this.text = text;
        this.showButton = showButton;
        this.isVisible = true;
        this._regularText = regularText;
    }
    setPanel(style: PopupWindowPanelStyle) {}

    hide() {
        this.isVisible = false;
    }
}

function usePopupWindowController(): [
    boolean,
    string,
    boolean,
    Material,
    () => void,
    Material,
] {
    const controller = useRef(PopupWindowController.instance);
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    const material = rotbMats.panelMaterialTexturedAlpha;
    const backgroundMaterial = useMemo(() => {
        const m = material!.clone() as FlatOpaqueTextured;
        m.setColor(parseColor('#404056EE'));
        m.setFlatTexture(rotbMats?.buttonSimple);
        return m;
    }, []);

    const lightTextMaterial = useMemo(() => {
        if (controller.current.regularText) {
            if (LightTextMaterial) return LightTextMaterial;
            const m = rotbMats.regularTextMaterial!.clone() as UIText;
            m.setColor(parseColor(colorSwatch.DescriptionText));
            LightTextMaterial = m;
            return m;
        } else {
            return mats.textMaterial;
        }
    }, []);

    const [isVisible, setIsVisible] = useState(false);
    const [text, setText] = useState('');
    const [showButton, setShowButton] = useState(true);

    useEffect(() => {
        if (!controller.current) return;
        const propertyChanged = (property: possibleProps) => {
            if (property === 'isVisible') {
                setIsVisible(controller.current.isVisible);
            }

            if (property === 'text') {
                setText(controller.current.text);
            }
            if (property === 'showButton') {
                setShowButton(controller.current.showButton);
            }
        };

        controller.current.propertyChanged.add(propertyChanged);

        return () => {
            controller.current.propertyChanged.remove(propertyChanged);
        };
    }, []);

    const onCloseClick = () => {
        if (controller.current) {
            controller.current.hide();
        }
    };

    return [
        isVisible,
        text,
        showButton,
        backgroundMaterial,
        onCloseClick,
        lightTextMaterial,
    ];
}

export const PopupWindow = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const [
        isVisible,
        text,
        showButton,
        backgroundMaterial,
        onCloseClick,
        regularTextMaterial,
    ] = usePopupWindowController();
    return (
        <>
            {isVisible && (
                <Container
                    position={PositionType.Absolute}
                    width="100%"
                    height="90%"
                    {...props}
                    justifyContent={Justify.Center}
                    alignItems={Align.Center}
                >
                    <Container>
                        <Panel9Slice
                            z={-0.001}
                            margin={10}
                            height={props.height ?? 200}
                            width={props.width ?? 500}
                            paddingLeft={8}
                            material={backgroundMaterial}
                            borderSize={8}
                            borderTextureSize={0.3}
                            alignItems={Align.Center}
                            alignContent={Align.Center}
                            justifyContent={Justify.Center}
                        >
                            <Text
                                width={props.width ?? 480}
                                // top={text.length < 40 ? 65 : 30}
                                textAlign="center"
                                fontSize={26}
                                textWrap="soft"
                                material={regularTextMaterial}
                            >
                                {text}
                            </Text>
                        </Panel9Slice>
                    </Container>
                    {showButton && (
                        <MenuButton
                            top={-36}
                            width={200}
                            height={48}
                            color={colorSwatch.AcceptButton}
                            fontSize={24}
                            onClick={() => onCloseClick()}
                        >
                            {Localization.instance.get('PurchaseFailedDialogAccept')}
                        </MenuButton>
                    )}
                </Container>
            )}
        </>
    );
};

PopupWindow.displayName = 'PopupWindow';
